// Advisor-only cost check for the AEP builder (Aziz 2026-10-16).
//
// Paul Maloney: a "stay" recommendation told the client his Wellcare PDP was
// "the most cost-effective drug plan" (the Prescriptions reason's sentence,
// aep-recommendation.js) while six PDPs in his quotes cost less at his own
// pharmacy. Nothing compared the kept plan to the quotes. This does, with the
// same yearly figure the summary shows: premium plus estimated drug costs at
// the client's cheapest pharmacy (store parsePlan: estimated_yearly_cost,
// cheapest_pharmacy; mail order only when the client prefers it), so the
// warning never disagrees with the copay table. A nudge for the advisor,
// never part of the client's summary or email.
import { money } from '~/util/string'

// Cheaper by less than this is noise, not a warning.
export const MIN_GAP = 50

const isPdp = p => !!p && (p.plan_type === 'pdp' || p.type === 'pdp')
const costOf = p => (Number.isFinite(p?.estimated_yearly_cost) ? p.estimated_yearly_cost : null)

export function pharmacyName (plan) {
    const ph = plan?.cheapest_pharmacy
    if (!ph) { return null }
    if (ph.mail_order || ph.pharmacy?.npi === 'mail_order') { return 'mail order' }
    return ph.pharmacy?.name || null
}

// The drug plans in `plans` that cost at least MIN_GAP less than `plan`, or
// null when there are none (or `plan` is not a priced drug plan).
export function cheaperPdps (plan, plans, { minGap = MIN_GAP } = {}) {
    if (!isPdp(plan)) { return null }
    const cost = costOf(plan)
    if (cost == null) { return null }
    const cheaper = (plans || [])
        .filter(p => isPdp(p) && p.id !== plan.id && costOf(p) != null && costOf(p) <= cost - minGap)
        .sort((a, b) => costOf(a) - costOf(b))
    if (!cheaper.length) { return null }
    return {
        cost,
        pharmacy: pharmacyName(plan),
        count: cheaper.length,
        cheapest: { name: cheaper[0].name, cost: costOf(cheaper[0]) },
    }
}

const m = v => money(Math.round(v))

export function pdpCostWarningText (plan, result, year, rxTicked) {
    if (!plan || !result) { return '' }
    const where = result.pharmacy ? ` at ${result.pharmacy}` : ''
    let s = `${plan.name} costs about ${m(result.cost)} for ${year} (premium plus estimated drug costs${where}). `
    s += result.count === 1
        ? `1 drug plan in this year's quotes costs less: ${result.cheapest.name} at ${m(result.cheapest.cost)}.`
        : `${result.count} drug plans in this year's quotes cost less; the cheapest is ${result.cheapest.name} at ${m(result.cheapest.cost)}.`
    if (rxTicked) {
        s += ' With Prescriptions ticked, the summary will tell the client this is the most cost-effective drug plan.'
    }
    return s
}
